export const CONVERSATION_USER_AUTH_HEADER = 'UserAuthenticationConversation'

export type ConversationAuthPair = {
  personId: string
  professionalId: string
}

function hexToKey32(raw: string): Uint8Array | null {
  const s = raw.trim()
  const hex = s.startsWith('0x') || s.startsWith('0X') ? s.slice(2) : s
  if (!/^[0-9a-fA-F]{64}$/.test(hex)) {
    return null
  }
  const out = new Uint8Array(32)
  for (let i = 0; i < 32; i += 1) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

/**
 * Resolve a chave AES-256 (32 bytes) de REACT_APP_CONVERSATIONS_ENCRYPT_KEY (hex)
 */
export function resolveConversationsAesKeyBytes(): Uint8Array | null {
  const hex = process.env.REACT_APP_CONVERSATIONS_ENCRYPT_KEY
  if (hex) {
    const k = hexToKey32(hex)
    if (k) {
      return k
    }
  }
  const b64 = process.env.REACT_APP_CONVERSATIONS_USER_AUTH_AES_KEY?.trim()
  if (b64) {
    try {
      const k = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      if (k.length === 32) {
        return k
      }
    } catch {
      // ignore
    }
  }
  return null
}

/**
 * Criptografa o par com AES-256-GCM
 * Retorna base64(IV_12bytes || ciphertext_with_tag)
 */
export async function encryptConversationAuthPayload(
  keyBytes: Uint8Array,
  pair: ConversationAuthPair
): Promise<string> {
  if (typeof window === 'undefined' || !globalThis.crypto?.subtle) {
    throw new Error('Web Crypto indisponível (use no cliente).')
  }
  if (keyBytes.length !== 32) {
    throw new Error('Chave AES-256: exatamente 32 bytes.')
  }
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  )
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const payload = {
    person_id: pair.personId,
    professional_id: pair.professionalId,
  }
  const plain = new TextEncoder().encode(JSON.stringify(payload))
  const buf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    plain
  )
  const out = new Uint8Array(iv.byteLength + buf.byteLength)
  out.set(iv, 0)
  out.set(new Uint8Array(buf), iv.byteLength)
  let bin = ''
  for (let i = 0; i < out.length; i += 1) {
    bin += String.fromCharCode(out[i]!)
  }
  return btoa(bin)
}

const MISSING_KEY_ERR =
  'REACT_APP_CONVERSATIONS_ENCRYPT_KEY (hex 64) ou REACT_APP_CONVERSATIONS_USER_AUTH_AES_KEY (base64, 32 bytes) é obrigatório para UserAuthenticationConversation.'

/**
 * Base64 de AES-256-GCM em cima de JSON { person_id, professional_id } — nunca token do storage.
 */
export async function getUserAuthenticationConversationHeader(
  pair: ConversationAuthPair
): Promise<string> {
  const keyBytes = resolveConversationsAesKeyBytes()
  if (!keyBytes) {
    throw new Error(MISSING_KEY_ERR)
  }
  return encryptConversationAuthPayload(keyBytes, pair)
}
